/**
 * Client-side photo watermark (Iter 214).
 *
 * Takes a File/Blob image and burns a forensic overlay into the bottom
 * band:
 *   TIMESTAMP        (ISO local time in bold caps)
 *   FULL ADDRESS     (site street, city, state)
 *   HEADING ARROW    (compass rose + degrees, if available)
 *
 * Runs in-browser via Canvas — no server round-trip. Returns a new File
 * (JPEG) suitable for the existing multipart upload flow.
 *
 * Called from PhotoAttachmentsField ONLY for field-report purposes
 * (incidents / DAR / maintenance). Officer HR docs, profile pics, and
 * site post-orders skip the watermark.
 */

const BAND_HEIGHT_PX = 140;
const PADDING = 18;
const FONT_STACK = "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif";

function _line(ctx, text, x, y, weight = "500", size = 16, color = "#ffffff") {
  ctx.fillStyle = color;
  ctx.font = `${weight} ${size}px ${FONT_STACK}`;
  ctx.fillText(text, x, y);
}

function _drawCompass(ctx, cx, cy, radius, headingDeg) {
  // Semi-transparent ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.5)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // North tick
  _line(ctx, "N", cx - 5, cy - radius - 4, "700", 11, "#FF3B30");

  // Arrow pointing at heading (0° = North / up)
  const rad = ((headingDeg - 90) * Math.PI) / 180;
  const tipX = cx + Math.cos(rad) * (radius - 4);
  const tipY = cy + Math.sin(rad) * (radius - 4);
  const tailX = cx - Math.cos(rad) * (radius - 8);
  const tailY = cy - Math.sin(rad) * (radius - 8);
  ctx.beginPath();
  ctx.moveTo(tailX, tailY);
  ctx.lineTo(tipX, tipY);
  ctx.strokeStyle = "#FF3B30";
  ctx.lineWidth = 3;
  ctx.stroke();

  // Arrowhead
  const headSize = 5;
  const angleLeft = rad + Math.PI - 0.4;
  const angleRight = rad + Math.PI + 0.4;
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(tipX + Math.cos(angleLeft) * headSize, tipY + Math.sin(angleLeft) * headSize);
  ctx.lineTo(tipX + Math.cos(angleRight) * headSize, tipY + Math.sin(angleRight) * headSize);
  ctx.closePath();
  ctx.fillStyle = "#FF3B30";
  ctx.fill();
}

/**
 * Read the device compass heading (0-360°, degrees clockwise from North).
 * Returns null when the browser doesn't support the API or the user
 * denies permission — the watermark degrades gracefully to text-only.
 */
export async function getHeading() {
  if (typeof window === "undefined") return null;
  if (typeof DeviceOrientationEvent === "undefined") return null;

  // iOS 13+ gates the sensor behind an explicit permission.
  if (typeof DeviceOrientationEvent.requestPermission === "function") {
    try {
      const perm = await DeviceOrientationEvent.requestPermission();
      if (perm !== "granted") return null;
    } catch {
      return null;
    }
  }

  return new Promise((resolve) => {
    let done = false;
    const finish = (v) => { if (!done) { done = true; window.removeEventListener("deviceorientation", onOrient); resolve(v); } };
    const onOrient = (e) => {
      // iOS/Safari exposes `webkitCompassHeading` already measured
      // clockwise-from-North (0° = pointing North, 90° = East). Use as-is.
      // Standard `alpha` is measured *counter*-clockwise from an arbitrary
      // reference — a 360-α flip approximates a clockwise-from-North
      // reading on Chromium desktop when the device is flat. iter216 fixed
      // an earlier bug that applied the flip to BOTH readings, inverting
      // the compass on every iOS-captured field photo.
      if (typeof e.webkitCompassHeading === "number" && Number.isFinite(e.webkitCompassHeading)) {
        finish(e.webkitCompassHeading);
        return;
      }
      const a = e.alpha;
      if (typeof a === "number" && Number.isFinite(a)) {
        finish(((360 - a) + 360) % 360);
      }
    };
    window.addEventListener("deviceorientation", onOrient);
    setTimeout(() => finish(null), 1500);
  });
}

/**
 * Watermark a photo File → returns a new JPEG File.
 *
 * @param {File|Blob} file            source image
 * @param {object}    opts
 * @param {string}    opts.timestamp  ISO string (defaults to now)
 * @param {string?}   opts.address    "123 Main St, City, ST" — pass site address for accuracy
 * @param {number?}   opts.lat        decimal degrees
 * @param {number?}   opts.lng        decimal degrees
 * @param {number?}   opts.headingDeg 0-360, from `getHeading()` or omitted
 * @param {string?}   opts.guardName  optional additional attribution line
 * @returns {Promise<File>}
 */
export async function watermarkPhoto(file, opts = {}) {
  const {
    timestamp = new Date().toISOString(),
    address,
    lat,
    lng,
    headingDeg,
    guardName,
  } = opts;

  // Load into an Image element via ObjectURL — avoids the extra base64 hop.
  const bmpUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Watermark: failed to decode image"));
      el.src = bmpUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // Bottom translucent band
    const band = Math.max(BAND_HEIGHT_PX, Math.round(img.naturalHeight * 0.13));
    const gradient = ctx.createLinearGradient(0, canvas.height - band, 0, canvas.height);
    gradient.addColorStop(0, "rgba(0,0,0,0)");
    gradient.addColorStop(0.35, "rgba(0,0,0,0.55)");
    gradient.addColorStop(1, "rgba(0,0,0,0.9)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, canvas.height - band, canvas.width, band);

    // Scale font sizes based on image width so 12MP and 720p both read well.
    const baseFont = Math.max(14, Math.round(canvas.width / 60));
    const ts = new Date(timestamp);
    const tsLabel = ts.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const x = PADDING;
    let y = canvas.height - band + Math.round(baseFont * 1.4);

    _line(ctx, tsLabel.toUpperCase(), x, y, "700", Math.round(baseFont * 1.15), "#FF3B30");
    y += Math.round(baseFont * 1.4);

    if (address) {
      _line(ctx, address, x, y, "500", baseFont, "#ffffff");
      y += Math.round(baseFont * 1.3);
    }

    const gps = (typeof lat === "number" && typeof lng === "number")
      ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
      : null;
    const meta = [gps, guardName].filter(Boolean).join("   ·   ");
    if (meta) {
      _line(ctx, meta, x, y, "400", Math.round(baseFont * 0.85), "#d4d4d8");
    }

    // Compass arrow (bottom-right corner of the band)
    if (typeof headingDeg === "number" && Number.isFinite(headingDeg)) {
      const size = Math.round(band * 0.35);
      _drawCompass(
        ctx,
        canvas.width - PADDING - size,
        canvas.height - band + size + PADDING / 2,
        size,
        headingDeg,
      );
      _line(
        ctx,
        `${Math.round(headingDeg)}°`,
        canvas.width - PADDING - size - Math.round(baseFont * 1.6),
        canvas.height - band + size + PADDING / 2 + Math.round(baseFont * 0.5),
        "600",
        Math.round(baseFont * 0.9),
        "#ffffff",
      );
    }

    // Encode. JPEG 0.85 keeps the file well under the 10 MB cap.
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.85),
    );
    if (!blob) {
      // toBlob returns null on certain WebView + tainted-canvas cases.
      // Fall back to the source File so the upload doesn't die.
      throw new Error("Watermark: canvas.toBlob returned null");
    }
    const outName = (file.name || "photo").replace(/\.[a-z0-9]+$/i, "") + "_wm.jpg";
    return new File([blob], outName, { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(bmpUrl);
  }
}

/**
 * Convenience: build a single-line address string from a Site record.
 * Returns null when no components are set so callers can decide whether
 * to fall back on the visitor's GPS reverse-geocode.
 */
export function formatSiteAddress(site) {
  if (!site) return null;
  const parts = [
    site.street_address,
    [site.city, site.state].filter(Boolean).join(", "),
    site.zip_code,
  ].filter(Boolean);
  const joined = parts.join(", ");
  return joined || site.address || null;
}
